import { c as _c } from "react/compiler-runtime";
import { IcoInfo24 } from '@onefootprint/icons';
import type { OnboardingRequirementCollectDocument } from '@onefootprint/request-types';
import { cx } from 'class-variance-authority';
import type React from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IDV_BODY_CONTENT_CONTAINER_ID } from '@/idv2/components/constants';
import { getLogger } from '@/idv2/utils/logger';
import useProcessImage, { isProcessingResultError } from '../../hooks/use-process-image';
import type { CameraErrorCause, CaptureKind, ReceivedImagePayload } from '../../types';
import { isDesktop, isDocument, isFace } from '../../utils/capture';
import Camera, { type ChildrenProps } from '../camera';
import AutoCaptureDoc from '../camera/auto-capture-doc';
import AutoCaptureFace from '../camera/auto-capture-face';
import type { AutoCaptureKind, CaptureStatus, DeviceKind } from '../camera/types';
import type { CameraSide } from '../camera/utils/get-camera-options';
import type { NavigationHeaderLeftButtonProps } from '../navigation-header';
import NavigationHeader from '../navigation-header';
import Instructions from './components/instructions';
import Preview from './components/preview';
import useCaptureTelemetry from './use-capture-telemetry';
type HeaderTextType = {
  camera: string;
  preview: string;
};
type PhotoCaptureProps = {
  autocaptureKind: AutoCaptureKind;
  autocapture?: boolean;
  cameraKind: CameraSide;
  deviceKind: DeviceKind;
  allowPdf: boolean;
  side?: 'front' | 'back';
  sideName?: string;
  docName?: string;
  requirement: OnboardingRequirementCollectDocument;
  hasBadConnectivity?: boolean;
  onCameraStuck?: () => void;
  onCameraErrored?: (cause: CameraErrorCause) => void;
  onCameraAccessDenied?: () => void;
  onBack?: () => void;
  onComplete: (payload: ReceivedImagePayload) => void;
  outlineHeightRatio: number; // with respect to the video width (not height)
  outlineWidthRatio: number; // with respect to the video width
  subtitle?: Partial<HeaderTextType>;
  title: HeaderTextType;
};
const DesktopNavProps: NavigationHeaderLeftButtonProps = {
  variant: 'close',
  confirmClose: true
};
const {
  logWarn,
  logInfo
} = getLogger({
  name: 'idv2',
  location: 'photo-capture'
});
const SheetContent = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="absolute bottom-10 left-0 right-0 z-10"><div className="bg-primary rounded-t-2xl p-6 shadow-xl">{children}</div></div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const FallbackSheet = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="absolute bottom-0 left-0 right-0 z-10"><div className="bg-primary rounded-t-2xl p-6 shadow-xl">{children}</div></div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const PhotoCapture = t0 => {
  const $ = _c(109);
  const {
    autocaptureKind,
    autocapture: t1,
    cameraKind,
    deviceKind,
    allowPdf,
    side,
    sideName,
    docName,
    requirement,
    hasBadConnectivity,
    onCameraStuck,
    onCameraErrored,
    onCameraAccessDenied,
    onBack,
    onComplete,
    outlineHeightRatio,
    outlineWidthRatio,
    subtitle,
    title
  } = t0;
  const autocapture = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "document-flow.components.photo-capture"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t2);
  const allowUpload = requirement.uploadSettings !== "capture_only_on_mobile" && requirement.uploadSettings !== "require_mobile_capture";
  let t3;
  if ($[1] !== allowPdf) {
    t3 = {
      allowPdf
    };
    $[1] = allowPdf;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    processImageUrl
  } = useProcessImage(t3);
  const [image, setImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [captureKind, setCaptureKind] = useState();
  const [showInstructions, setShowInstructions] = useState(false);
  const [isCaptured, setIsCaptured] = useState(false);
  const [autocaptureFeedback, setAutocaptureFeedback] = useState("detecting");
  let t4;
  if ($[3] !== autocapture || $[4] !== autocaptureFeedback || $[5] !== autocaptureKind || $[6] !== side) {
    t4 = {
      autocapture,
      autocaptureKind,
      side,
      status: autocaptureFeedback
    };
    $[3] = autocapture;
    $[4] = autocaptureFeedback;
    $[5] = autocaptureKind;
    $[6] = side;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    reportCapture,
    reportCameraStuck
  } = useCaptureTelemetry(t4);
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      logInfo("Retaking photo");
      setImage(null);
      setIsCaptured(false);
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleRetake = t5;
  let t6;
  if ($[9] !== captureKind || $[10] !== hasBadConnectivity || $[11] !== image || $[12] !== onComplete || $[13] !== processImageUrl) {
    t6 = async () => {
      if (!image) {
        logWarn("Captured image could not be confirmed and submitted - retaking the image");
        return;
      }
      if (!captureKind) {
        logWarn("Captured kind could not be determined - retaking the image");
        return;
      }
      setIsLoading(true);
      const processResult = await processImageUrl(image, hasBadConnectivity);
      if (isProcessingResultError(processResult)) {
        setIsLoading(false);
        handleRetake();
        logWarn("Captured image could not be processed - retaking the image");
        return;
      }
      setIsLoading(false);
      const {
        file,
        extraCompressed
      } = processResult;
      onComplete({
        captureKind,
        extraCompressed,
        imageFile: file,
        forcedUpload: false
      });
    };
    $[9] = captureKind;
    $[10] = hasBadConnectivity;
    $[11] = image;
    $[12] = onComplete;
    $[13] = processImageUrl;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleConfirm = t6;
  let t7;
  if ($[15] !== onCameraErrored) {
    t7 = cause => {
      logInfo("Camera error occurred", {
        cause
      });
      onCameraErrored?.(cause);
    };
    $[15] = onCameraErrored;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleError = t7;
  let t8;
  if ($[17] !== reportCapture) {
    t8 = (newImage, newCaptureKind) => {
      reportCapture(newCaptureKind);
      setImage(newImage);
      setCaptureKind(newCaptureKind);
    };
    $[17] = reportCapture;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleCapture = t8;
  let t9;
  if ($[19] !== onComplete || $[20] !== reportCapture) {
    t9 = payload => {
      reportCapture(payload.captureKind);
      onComplete(payload);
    };
    $[19] = onComplete;
    $[20] = reportCapture;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const handleUpload = t9;
  let t10;
  if ($[22] !== onCameraStuck || $[23] !== reportCameraStuck) {
    t10 = () => {
      reportCameraStuck();
      onCameraStuck?.();
      logInfo("Photocapture: Camera stuck, fallback to doc upload");
    };
    $[22] = onCameraStuck;
    $[23] = reportCameraStuck;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const handleCameraStuck = t10;
  let t11;
  if ($[25] !== autocapture || $[26] !== autocaptureKind || $[27] !== isCaptured) {
    t11 = t12 => {
      const {
        canvasAutoCaptureRef,
        feedbackPositionFromBottom,
        videoResolution,
        onDetectionComplete,
        onDetectionReset,
        outlineHeight,
        outlineWidth,
        videoRef,
        videoSize
      } = t12;
      if (!autocapture) {
        return null;
      }
      if (isDocument(autocaptureKind)) {
        return <AutoCaptureDoc canvasAutoCaptureRef={canvasAutoCaptureRef} feedbackPositionFromBottom={feedbackPositionFromBottom} isCaptured={isCaptured} videoResolution={videoResolution} onDetectionComplete={onDetectionComplete} onDetectionReset={onDetectionReset} outlineHeight={outlineHeight} outlineWidth={outlineWidth} setAutoCaptureFeedback={setAutocaptureFeedback} videoRef={videoRef} videoSize={videoSize} />;
      }
      return isFace(autocaptureKind) ? <AutoCaptureFace canvasAutoCaptureRef={canvasAutoCaptureRef} feedbackPositionFromBottom={feedbackPositionFromBottom} isCaptured={isCaptured} onDetectionComplete={onDetectionComplete} onDetectionReset={onDetectionReset} outlineWidth={outlineWidth} setAutoCaptureFeedback={setAutocaptureFeedback} videoRef={videoRef} videoSize={videoSize} videoResolution={videoResolution} /> : null;
    };
    $[25] = autocapture;
    $[26] = autocaptureKind;
    $[27] = isCaptured;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const renderCameraChildren = t11;
  let t12;
  if ($[29] !== deviceKind) {
    t12 = isDesktop(deviceKind);
    $[29] = deviceKind;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const desktop = t12;
  const showOverlay = !!(image || isLoading);
  let t13;
  let t14;
  if ($[31] !== showOverlay) {
    t13 = () => {
      if (!showOverlay) {
        return;
      }
      const target = document.getElementById(IDV_BODY_CONTENT_CONTAINER_ID)?.parentElement;
      if (target && getComputedStyle(target).position === "static") {
        target.style.position = "relative";
      }
    };
    t14 = [showOverlay];
    $[31] = showOverlay;
    $[32] = t13;
    $[33] = t14;
  } else {
    t13 = $[32];
    t14 = $[33];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  let t17;
  let t18;
  if ($[34] !== allowPdf || $[35] !== allowUpload || $[36] !== autocapture || $[37] !== autocaptureFeedback || $[38] !== autocaptureKind || $[39] !== cameraKind || $[40] !== desktop || $[41] !== deviceKind || $[42] !== docName || $[43] !== handleCameraStuck || $[44] !== handleCapture || $[45] !== handleConfirm || $[46] !== handleError || $[47] !== handleUpload || $[48] !== hasBadConnectivity || $[49] !== image || $[50] !== isCaptured || $[51] !== isLoading || $[52] !== onBack || $[53] !== onCameraAccessDenied || $[54] !== outlineHeightRatio || $[55] !== outlineWidthRatio || $[56] !== renderCameraChildren || $[57] !== showOverlay || $[58] !== sideName || $[59] !== subtitle || $[60] !== t || $[61] !== title.camera) {
    const sheetPortalTarget = showOverlay ? document.getElementById(IDV_BODY_CONTENT_CONTAINER_ID)?.parentElement ?? null : null;
    if ($[66] !== desktop || $[67] !== onBack || $[68] !== subtitle || $[69] !== title.camera) {
      t18 = desktop ? <div className="mb-6"><NavigationHeader leftButton={DesktopNavProps} /><div className="flex flex-col items-center gap-2 text-center"><h2 className="text-heading-3 text-primary">{title.camera}</h2>{subtitle?.camera && <p className="text-body-2 text-secondary">{subtitle.camera}</p>}</div></div> : <NavigationHeader leftButton={{
        variant: "back",
        onBack
      }} rightButton={{
        icon: IcoInfo24,
        onClick: () => setShowInstructions(true)
      }} content={{
        kind: "static",
        title: title.camera
      }} />;
      $[66] = desktop;
      $[67] = onBack;
      $[68] = subtitle;
      $[69] = title.camera;
      $[70] = t18;
    } else {
      t18 = $[70];
    }
    t15 = "relative w-full flex-1 min-h-0";
    const t19 = showOverlay && "blur-sm pointer-events-none";
    let t20;
    if ($[71] !== t19) {
      t20 = cx("w-full h-full transition-[filter]", t19);
      $[71] = t19;
      $[72] = t20;
    } else {
      t20 = $[72];
    }
    let t21;
    if ($[73] !== allowPdf || $[74] !== allowUpload || $[75] !== autocapture || $[76] !== autocaptureFeedback || $[77] !== autocaptureKind || $[78] !== cameraKind || $[79] !== deviceKind || $[80] !== docName || $[81] !== handleCameraStuck || $[82] !== handleCapture || $[83] !== handleError || $[84] !== handleUpload || $[85] !== hasBadConnectivity || $[86] !== isCaptured || $[87] !== onCameraAccessDenied || $[88] !== outlineHeightRatio || $[89] !== outlineWidthRatio || $[90] !== renderCameraChildren || $[91] !== sideName) {
      t21 = <Camera autocapture={autocapture} autoCaptureFeedback={autocaptureFeedback} autoCaptureKind={autocaptureKind} cameraSide={cameraKind} deviceKind={deviceKind} docName={docName} sideName={sideName} onCapture={handleCapture} onUpload={handleUpload} hasBadConnectivity={hasBadConnectivity} onCameraError={handleError} onCameraAccessDenied={onCameraAccessDenied} outlineHeightRatio={outlineHeightRatio} outlineWidthRatio={outlineWidthRatio} isCaptured={isCaptured} setIsCaptured={setIsCaptured} allowPdf={allowPdf} onCameraStuck={handleCameraStuck} allowUpload={allowUpload}>{renderCameraChildren}</Camera>;
      $[73] = allowPdf;
      $[74] = allowUpload;
      $[75] = autocapture;
      $[76] = autocaptureFeedback;
      $[77] = autocaptureKind;
      $[78] = cameraKind;
      $[79] = deviceKind;
      $[80] = docName;
      $[81] = handleCameraStuck;
      $[82] = handleCapture;
      $[83] = handleError;
      $[84] = handleUpload;
      $[85] = hasBadConnectivity;
      $[86] = isCaptured;
      $[87] = onCameraAccessDenied;
      $[88] = outlineHeightRatio;
      $[89] = outlineWidthRatio;
      $[90] = renderCameraChildren;
      $[91] = sideName;
      $[92] = t21;
    } else {
      t21 = $[92];
    }
    if ($[93] !== t20 || $[94] !== t21) {
      t16 = <div className={t20}>{t21}</div>;
      $[93] = t20;
      $[94] = t21;
      $[95] = t16;
    } else {
      t16 = $[95];
    }
    t17 = image && (sheetPortalTarget ? createPortal(<SheetContent><Preview imageSrc={image} onRetake={handleRetake} onConfirm={handleConfirm} isLoading={isLoading} cameraKind={cameraKind} deviceKind={deviceKind} title={isDocument(autocaptureKind) ? t(_temp) : t(_temp2)} /></SheetContent>, sheetPortalTarget) : <FallbackSheet><Preview imageSrc={image} onRetake={handleRetake} onConfirm={handleConfirm} isLoading={isLoading} cameraKind={cameraKind} deviceKind={deviceKind} title={isDocument(autocaptureKind) ? t(_temp3) : t(_temp4)} /></FallbackSheet>);
    $[34] = allowPdf;
    $[35] = allowUpload;
    $[36] = autocapture;
    $[37] = autocaptureFeedback;
    $[38] = autocaptureKind;
    $[39] = cameraKind;
    $[40] = desktop;
    $[41] = deviceKind;
    $[42] = docName;
    $[43] = handleCameraStuck;
    $[44] = handleCapture;
    $[45] = handleConfirm;
    $[46] = handleError;
    $[47] = handleUpload;
    $[48] = hasBadConnectivity;
    $[49] = image;
    $[50] = isCaptured;
    $[51] = isLoading;
    $[52] = onBack;
    $[53] = onCameraAccessDenied;
    $[54] = outlineHeightRatio;
    $[55] = outlineWidthRatio;
    $[56] = renderCameraChildren;
    $[57] = showOverlay;
    $[58] = sideName;
    $[59] = subtitle;
    $[60] = t;
    $[61] = title.camera;
    $[62] = t15;
    $[63] = t16;
    $[64] = t17;
    $[65] = t18;
  } else {
    t15 = $[62];
    t16 = $[63];
    t17 = $[64];
    t18 = $[65];
  }
  let t19;
  if ($[96] !== t15 || $[97] !== t16 || $[98] !== t17) {
    t19 = <div className={t15}>{t16}{t17}</div>;
    $[96] = t15;
    $[97] = t16;
    $[98] = t17;
    $[99] = t19;
  } else {
    t19 = $[99];
  }
  let t20;
  if ($[100] !== autocapture || $[101] !== autocaptureKind || $[102] !== desktop || $[103] !== showInstructions) {
    t20 = !desktop && <Instructions onClose={() => setShowInstructions(false)} isOpen={showInstructions} autoCaptureKind={autocaptureKind} autocapture={autocapture} />;
    $[100] = autocapture;
    $[101] = autocaptureKind;
    $[102] = desktop;
    $[103] = showInstructions;
    $[104] = t20;
  } else {
    t20 = $[104];
  }
  let t21;
  if ($[105] !== t18 || $[106] !== t19 || $[107] !== t20) {
    t21 = <>{t18}{t19}{t20}</>;
    $[105] = t18;
    $[106] = t19;
    $[107] = t20;
    $[108] = t21;
  } else {
    t21 = $[108];
  }
  return t21;
};
export default PhotoCapture;
function _temp($) {
  return $.preview.titleDocument;
}
function _temp2($_0) {
  return $_0.preview.titleFace;
}
function _temp3($_1) {
  return $_1.preview.titleDocument;
}
function _temp4($_2) {
  return $_2.preview.titleFace;
}